import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Calculator,
  Search,
  Scale,
  Briefcase,
  Landmark,
  Shield,
  FileText,
  Sparkles,
  ChevronRight,
  Coins
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

// Import Custom Scraped Form Components
import {
  SentenciaAdministrativaForm,
  PrivacionInjustaForm,
  MoraContratosEstatalesForm
} from "./components/AdministrativoCalculators";

import {
  CompetenciaForm,
  PlazosProcesalesForm
} from "./components/ProcesalCalculators";

import {
  IndexacionForm,
  CuotaAlimentariaForm,
  CanonesArrendamientoForm,
  MoraPHForm
} from "./components/CivilCalculators";

import {
  LiquidacionLaboralForm,
  PensionLey797Form,
  PensionInvalidezForm,
  RetroactivoPensionalForm,
  PerjuiciosTrasladoPensionalForm
} from "./components/LaboralCalculators";

import {
  VencimientoTerminosForm,
  DosificacionPunitivaForm
} from "./components/PenalCalculators";

export interface CalculatorItem {
  id: string;
  title: string;
  category: "administrativo" | "procesal" | "civil" | "laboral" | "penal";
  badge: string;
  description: string;
  norma: string;
}

const CALCULATORS_LIST: CalculatorItem[] = [
  // Administrativo (3)
  {
    id: "sentencia-administrativa",
    title: "Sentencias Administrativas (Liquidación del Daño)",
    category: "administrativo",
    badge: "CPACA Art. 192",
    description: "Liquidación de condenas contra el Estado. 10 meses plazo de gracia, indexación IPC + intereses DTF y mora comercial.",
    norma: "CPACA Art. 192 y 195"
  },
  {
    id: "privacion-injusta",
    title: "Privación Injusta de la Libertad",
    category: "administrativo",
    badge: "Reparación Directa",
    description: "Indemnización por detención preventiva injusta. Daño moral a víctima directa y familiares según jurisprudencia del Consejo de Estado.",
    norma: "Ley 270 de 1996 Art. 65"
  },
  {
    id: "mora-contratos-estatales",
    title: "Mora en Contratos Estatales",
    category: "administrativo",
    badge: "Ley 80 / Dec 679",
    description: "Actualización del valor histórico (IPC) + intereses de mora del 1% mensual por incumplimiento de pago estatal.",
    norma: "Ley 80 de 1993 Art. 4"
  },

  // Procesal General (2)
  {
    id: "competencia",
    title: "Competencia y Cuantía",
    category: "procesal",
    badge: "CGP / CPACA",
    description: "Determina el juez competente por cuantía (Mínima, Menor, Mayor) en materia civil, familia, laboral y administrativa.",
    norma: "CGP Art. 25 y 26"
  },
  {
    id: "plazos",
    title: "Cómputo de Plazos Procesales",
    category: "procesal",
    badge: "CGP / Ley 2213",
    description: "Calendario de vencimientos de plazos procesales descartando días hábiles, inhábiles, festivos y vacancia judicial.",
    norma: "CGP Art. 118 y Ley 2213"
  },

  // Civil & Familia (9)
  {
    id: "indexacion",
    title: "Indexación de Valores (IPC)",
    category: "civil",
    badge: "DANE / IPC",
    description: "Actualiza valores históricos al presente usando el IPC acumulado del DANE. Fórmula judicial estándar.",
    norma: "Jurisprudencia CSJ Sala Civil"
  },
  {
    id: "cuota-alimentaria",
    title: "Liquidación de Cuota Alimentaria",
    category: "civil",
    badge: "Alimentos",
    description: "Cuotas alimentarias adeudadas con intereses de mora e incrementos pactados o por IPC/SMLMV.",
    norma: "Ley 1098 de 2006 Art. 129"
  },
  {
    id: "canones-arrendamiento",
    title: "Cánones de Arrendamiento en Mora",
    category: "civil",
    badge: "Arrendamiento",
    description: "Cánones adeudados, incrementos anuales pactados (IPC Ley 820) e intereses en procesos de restitución.",
    norma: "Ley 820 de 2003"
  },
  {
    id: "mora-ph",
    title: "Mora Propiedad Horizontal",
    category: "civil",
    badge: "Ley 675/2001",
    description: "Cuotas de administración en mora con 1.5 veces el interés bancario corriente mes a mes.",
    norma: "Ley 675 de 2001 Art. 30"
  },

  // Laboral & S.S. (11)
  {
    id: "perjuicios-traslado-pensional",
    title: "Perjuicios por Traslado Pensional (Pérdida de Oportunidad)",
    category: "laboral",
    badge: "CSJ Sala Laboral",
    description: "Indemnización por ineficacia del traslado pensional según la fórmula oficial de la Corte Suprema de Justicia: PP × (MesadaRPM − MesadaRAIS) × (EVaños × 13) y tabla de rentistas.",
    norma: "CSJ Sala de Casación Laboral — Relatoría"
  },
  {
    id: "liquidacion-laboral",
    title: "Liquidación Laboral Completa",
    category: "laboral",
    badge: "CST",
    description: "Cesantías, intereses, prima, vacaciones, indemnización por despido (Art. 64) y mora (Art. 65 CST).",
    norma: "CST Art. 64, 65, 249"
  },
  {
    id: "pension-ley797",
    title: "Pensión de Vejez — Ley 797",
    category: "laboral",
    badge: "Ley 797/2003",
    description: "Cumplimiento de requisitos (62H / 57M, 1.300 semanas). Tasa de reemplazo y mesada pensional.",
    norma: "Ley 797 de 2003 Art. 9"
  },
  {
    id: "pension-invalidez",
    title: "Pensión de Invalidez",
    category: "laboral",
    badge: "Ley 860/2003",
    description: "Requisitos (PCL ≥ 50% y semanas). Desglose por origen común o laboral ARL y ley 776.",
    norma: "Ley 860 de 2003"
  },
  {
    id: "retroactivo-pensional",
    title: "Retroactivo Pensional",
    category: "laboral",
    badge: "Pensional",
    description: "Mesadas dejadas de pagar con indexación IPC o intereses Art. 141 Ley 100 e inclusión de mesadas 13 y 14.",
    norma: "Ley 100 de 1993 Art. 141"
  },

  // Penal (2)
  {
    id: "vencimiento-terminos",
    title: "Vencimiento de Términos",
    category: "penal",
    badge: "Ley 906/2004",
    description: "Términos procesales penales para libertad por vencimiento. Arts. 292, 294 y 317 de la Ley 906/2004.",
    norma: "Ley 906 de 2004 Art. 317"
  },
  {
    id: "dosificacion-punitiva",
    title: "Dosificación Punitiva",
    category: "penal",
    badge: "Ley 599/2000",
    description: "Sistema de cuartos, modificadores, atenuantes, agravantes y rebajas por preacuerdo o allanamiento.",
    norma: "Ley 599 de 2000 Art. 60 y 61"
  }
];

export interface CalculadorasHubProps {
  initialCalculatorId?: string;
}

export const CalculadorasHub: React.FC<CalculadorasHubProps> = ({ initialCalculatorId }) => {
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("todos");
  const [activeCalculator, setActiveCalculator] = useState<CalculatorItem | null>(null);

  useEffect(() => {
    const targetId = initialCalculatorId || searchParams.get("calc");
    if (targetId) {
      const found = CALCULATORS_LIST.find((c) => c.id === targetId);
      if (found) {
        setActiveCalculator(found);
        setSelectedCategory(found.category);
      }
    }
  }, [initialCalculatorId, searchParams]);

  const filteredCalculators = CALCULATORS_LIST.filter((calc) => {
    const matchesSearch =
      calc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      calc.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      calc.badge.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesCategory =
      selectedCategory === "todos" || calc.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  const getCategoryTheme = (cat: string) => {
    switch (cat) {
      case "administrativo":
        return { bg: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: Landmark };
      case "procesal":
        return { bg: "bg-violet-50 text-violet-700 border-violet-200", icon: FileText };
      case "civil":
        return { bg: "bg-blue-50 text-blue-700 border-blue-200", icon: Scale };
      case "laboral":
        return { bg: "bg-amber-50 text-amber-800 border-amber-200", icon: Briefcase };
      case "penal":
        return { bg: "bg-red-50 text-red-700 border-red-200", icon: Shield };
      default:
        return { bg: "bg-slate-100 text-slate-700 border-slate-200", icon: Calculator };
    }
  };

  const renderCalculatorForm = (id: string) => {
    switch (id) {
      // Administrativo
      case "sentencia-administrativa":
        return <SentenciaAdministrativaForm />;
      case "privacion-injusta":
        return <PrivacionInjustaForm />;
      case "mora-contratos-estatales":
        return <MoraContratosEstatalesForm />;

      // Procesal
      case "competencia":
        return <CompetenciaForm />;
      case "plazos":
        return <PlazosProcesalesForm />;

      // Civil
      case "indexacion":
        return <IndexacionForm />;
      case "cuota-alimentaria":
        return <CuotaAlimentariaForm />;
      case "canones-arrendamiento":
        return <CanonesArrendamientoForm />;
      case "mora-ph":
        return <MoraPHForm />;

      // Laboral
      case "perjuicios-traslado-pensional":
        return <PerjuiciosTrasladoPensionalForm />;
      case "liquidacion-laboral":
        return <LiquidacionLaboralForm />;
      case "pension-ley797":
        return <PensionLey797Form />;
      case "pension-invalidez":
        return <PensionInvalidezForm />;
      case "retroactivo-pensional":
        return <RetroactivoPensionalForm />;

      // Penal
      case "vencimiento-terminos":
        return <VencimientoTerminosForm />;
      case "dosificacion-punitiva":
        return <DosificacionPunitivaForm />;

      default:
        return <SentenciaAdministrativaForm />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 p-6 md:p-10 space-y-8">
      {/* Header Banner - Light Modern Style */}
      <div className="relative overflow-hidden rounded-3xl bg-white border border-slate-200 p-8 md:p-12 shadow-sm text-slate-900">
        <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-3xl space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs font-bold uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5 text-amber-600" />
              Herramientas Jurídicas Legalia
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-extrabold uppercase">
              <Coins className="w-3.5 h-3.5 text-emerald-600" />
              Costo: 1 Crédito por Cálculo
            </span>
          </div>

          <h1 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight uppercase font-display">
            Calculadoras Jurídicas
          </h1>
          <p className="text-slate-600 text-base md:text-lg leading-relaxed">
            Plataforma con desgloses detallados, tarjetas explicativas del marco legal, periodos de liquidación y preguntas frecuentes por cada herramienta.
          </p>

          {/* Search bar */}
          <div className="pt-4 relative max-w-xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Buscar calculadora (ej. liquidación del daño, IPC, cuota alimentaria)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 py-6 bg-slate-50 border-slate-200 text-slate-900 rounded-2xl placeholder:text-slate-400 focus:bg-white focus:border-amber-500 focus:ring-amber-500/20 shadow-sm"
            />
          </div>
        </div>
      </div>

      {/* Category Tabs & Filter */}
      <Tabs defaultValue="todos" value={selectedCategory} onValueChange={setSelectedCategory} className="w-full">
        <TabsList className="bg-white border border-slate-200 p-1.5 rounded-2xl flex flex-wrap h-auto gap-2 shadow-sm">
          <TabsTrigger value="todos" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white font-bold rounded-xl text-xs uppercase px-4 py-2 text-slate-600">
            Todos ({CALCULATORS_LIST.length})
          </TabsTrigger>
          <TabsTrigger value="administrativo" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white font-bold rounded-xl text-xs uppercase px-4 py-2 text-slate-600">
            Administrativo
          </TabsTrigger>
          <TabsTrigger value="procesal" className="data-[state=active]:bg-violet-600 data-[state=active]:text-white font-bold rounded-xl text-xs uppercase px-4 py-2 text-slate-600">
            Procesal General
          </TabsTrigger>
          <TabsTrigger value="civil" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white font-bold rounded-xl text-xs uppercase px-4 py-2 text-slate-600">
            Civil & Familia
          </TabsTrigger>
          <TabsTrigger value="laboral" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white font-bold rounded-xl text-xs uppercase px-4 py-2 text-slate-600">
            Laboral & S.S.
          </TabsTrigger>
          <TabsTrigger value="penal" className="data-[state=active]:bg-red-600 data-[state=active]:text-white font-bold rounded-xl text-xs uppercase px-4 py-2 text-slate-600">
            Penal
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Calculators Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCalculators.map((calc) => {
          const theme = getCategoryTheme(calc.category);
          const CategoryIcon = theme.icon;

          return (
            <Card
              key={calc.id}
              onClick={() => setActiveCalculator(calc)}
              className="bg-white border-slate-200 hover:border-slate-300 hover:shadow-lg transition-all duration-300 rounded-2xl cursor-pointer group flex flex-col justify-between"
            >
              <CardHeader className="space-y-3 pb-3">
                <div className="flex items-center justify-between">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-extrabold uppercase tracking-wider border ${theme.bg}`}>
                    <CategoryIcon className="w-3.5 h-3.5" />
                    {calc.badge}
                  </span>
                  <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-800 text-[10px] font-bold flex items-center gap-1">
                    <Coins className="w-3 h-3 text-amber-600" />
                    1 Crédito
                  </Badge>
                </div>
                <CardTitle className="text-lg font-bold text-slate-900 group-hover:text-blue-600 transition-colors leading-snug">
                  {calc.title}
                </CardTitle>
                <CardDescription className="text-xs text-slate-500 leading-relaxed">
                  {calc.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-slate-700 group-hover:text-blue-600 group-hover:translate-x-1 transition-all">
                  <span className="uppercase tracking-wider">Ver Detalle y Calcular →</span>
                  <ChevronRight className="w-4 h-4" />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Detailed Modal View for Selected Calculator */}
      {activeCalculator && (
        <Dialog open={Boolean(activeCalculator)} onOpenChange={(open) => !open && setActiveCalculator(null)}>
          <DialogContent className="max-w-4xl bg-slate-50 text-slate-900 border-slate-200 rounded-3xl max-h-[92vh] overflow-y-auto shadow-2xl p-6 md:p-8">
            <DialogHeader className="space-y-2 border-b border-slate-200 pb-4 bg-white -mx-6 md:-mx-8 -mt-6 md:-mt-8 p-6 md:p-8 rounded-t-3xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge className="bg-slate-900 text-white font-bold uppercase text-[10px]">
                    {activeCalculator.badge}
                  </Badge>
                  <span className="text-xs text-slate-500 font-medium">{activeCalculator.norma}</span>
                </div>
                <Badge className="bg-amber-100 text-amber-900 border-amber-300 font-extrabold uppercase text-[10px] flex items-center gap-1">
                  <Coins className="w-3.5 h-3.5 text-amber-700" /> 1 Crédito por cálculo
                </Badge>
              </div>
              <DialogTitle className="text-2xl md:text-3xl font-black text-slate-900 font-display">
                {activeCalculator.title}
              </DialogTitle>
              <DialogDescription className="text-slate-600 text-sm">
                {activeCalculator.description}
              </DialogDescription>
            </DialogHeader>

            {/* Custom Scraped Detailed Form Render */}
            <div className="py-4">
              {renderCalculatorForm(activeCalculator.id)}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default CalculadorasHub;
